import { useQuery } from '@tanstack/react-query';
import { ONE_DAY_IN_MILLISECONDS, THREE_MINUTES_IN_MILLISECONDS } from 'mdc-constants';
import { useCallback } from 'react';
import { useTypedOpenApi } from '../../providers';
import type { Schemas } from '../generated/laravel-typed-openapi';
import { getUserSettings } from '../users/userSettingsApi';

/** The settings this hook is asked for by key. The resource carries more; read `data` for those. */
type SettingsKeyType =
  | 'date_format'
  | 'time_format'
  | 'first_day_of_week'
  | 'unit'
  | 'decimal_symbol'
  | 'digit_grouping_symbol';

/**
 * The key every reader and every write shares.
 *
 * Deliberately the one the Lumen `userSettings` query used: the mutation hooks already invalidate
 * it, and nothing had to be found and changed when the source behind it moved to REST.
 */
export const USER_SETTINGS_QUERY_KEY = ['userSettings'];

/**
 * How long the stored settings are trusted for.
 *
 * Anything above zero is what stops a refetch on the way into every screen — forty-odd components
 * read this hook, and the default `staleTime` had each mount asking again. Minutes rather than the
 * hours `CURRENT_USER_CACHE` uses, because settings are the one resource the user edits from two
 * clients: change the unit on the phone and the dashboard should not spend an afternoon formatting
 * against the old one. Every in-app write invalidates the key, so this window only ever covers a
 * change made somewhere else.
 *
 * `gcTime` is long on purpose, and separately: the cost widgets read the currency symbol out of the
 * persisted cache so it survives a cold start offline.
 */
export const USER_SETTINGS_CACHE = {
  staleTime: THREE_MINUTES_IN_MILLISECONDS,
  gcTime: ONE_DAY_IN_MILLISECONDS,
} as const;

/**
 * What every setting falls back to until the request has answered.
 *
 * At module scope rather than rebuilt per render: React Query only reuses the previous placeholder
 * result when the value it was given is the same reference, so a fresh object each render would
 * hand every consumer a new `data` identity while the query is still pending.
 */
const PLACEHOLDER_DATA = {
  unit: 'metric',
  timezone_id: null,
  language_id: null,
  // No language: nothing that follows the stored language acts on the placeholder.
  language: null,
  currency_id: null,
  base_currency_id: '',
  date_format: 'DD/MM/Y',
  time_format: 'HH:mm',
  first_day_of_week: 1,
  decimal_symbol: '.',
  digit_grouping_symbol: ',',
  currency: null,
  base_currency: null,
  timezone: null,
} satisfies Schemas.SettingsResource;

/** The stored `date_format` values that aren't dayjs tokens as written. */
const DATE_FORMAT_MAP = new Map([
  ['DD/MM/Y', 'DD/MM/YYYY'],
  ['Y-MM-DD', 'YYYY-MM-DD'],
  ['D/M/Y', 'D/M/YYYY'],
]);

/**
 * Everything the account has set, from `GET /users/settings`.
 *
 * One request for what used to take two: the units, formats and symbols that live in the settings
 * table, and the time zone and currencies that used to be read off the user. `getSettingByKey`
 * answers exactly as it did when this read the Lumen `userSettings` query — the same keys, the same
 * expanded date formats, the same strings — so the formatters and tables built on it did not have
 * to change when the source did.
 *
 * `enabled` is for screens a signed-out visitor can reach — TheBoatDB's public boat pages. With
 * no session the endpoint can only answer 401, and React Query retries it three times, so every
 * anonymous page view (crawlers included) cost four failed requests. Disabled, the hook answers
 * from the placeholder, which is what those pages rendered anyway.
 */
export const useUserSettings = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { apiClient } = useTypedOpenApi();

  const queryInfo = useQuery({
    queryKey: USER_SETTINGS_QUERY_KEY,
    queryFn: () => getUserSettings(apiClient),
    placeholderData: PLACEHOLDER_DATA,
    enabled,
    ...USER_SETTINGS_CACHE,
  });

  const { data } = queryInfo;

  /**
   * Memoized on the data it reads, so it only changes identity when a setting actually changes.
   * Callers put it straight into `useMemo`/`useCallback` deps — `useFormattedValues` and every
   * table column built from it — and a new function each render rebuilds those, which remounts the
   * components underneath and refetches this query on every mount.
   */
  const getSettingByKey = useCallback(
    (settingKey: SettingsKeyType): string | undefined => {
      const value = (data ?? PLACEHOLDER_DATA)[settingKey];

      // `first_day_of_week` is a number on the resource and a string everywhere it is used — the
      // option lists are built from `'0'`/`'1'` — so every key answers as text.
      const stored = value === null || value === undefined ? undefined : String(value);

      if (settingKey === 'date_format' && stored) {
        return DATE_FORMAT_MAP.get(stored) ?? stored;
      }

      return stored || undefined;
    },
    [data],
  );

  return { ...queryInfo, getSettingByKey };
};

export default useUserSettings;
